/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */
import React from 'react';
import { noop, isEmpty } from 'lodash';
import { footerLinks } from 'services/Externals';
import TRP from 'components/shared/TRP/TRP';

export const onlySpacesRegex = /^\s+$/;
const NAME_REGEX = /^(([ ]*)|([ ]*[A-Za-z0-9]+[ A-Za-z0-9\-_.,'&amp;]*))$/i;
/* eslint-disable */
export const BENE_NAME_REGEX = /^(([ ]*)|([ ]*[A-Za-z0-9]+[ A-Za-z0-9\-_.,'&amp;\(\)]*))$/i;
/* eslint-disable */
export const BENE_ENTITY_NAME_REGEX = /^(([ ]*)|([ ]*[A-Za-z0-9]+[ A-Za-z0-9\-_.,'&amp;\(\)\/]*))$/i;
/* eslint-disable */

const ADDRESS_LINE1_REGEX =
  /^(([ ]*)|(?=.*?[A-Za-z])([ A-Za-z0-9\-_/.":,'&#()%`$+*=\\{}\[\];&amp;]+))$/i; // ADDRESS_LINE_REGEX

const ADDRESS_LINE2_REGEX =
  /^(([ ]*)|([ A-Za-z0-9\-_/.":,'&#()%`$+*=\\{}\[\];&amp;]+))$/i;
/* eslint-enable */
const REPETITION_REGEX = /^(\d)\1*$/;
export const GVA_NAME_REGEX = /^[A-Za-z0-9]+[ A-Za-z0-9-_.,'&]*$/; // FIRST_NAME & LAST NAME
const PHONE_REGEX = /^[0-9]+$/; // PHONE
const RELATIONSHIP_REGEX =
  /^(([ ]*)|([a-zA-Z0-9-.,:/+=&amp;%$#_!~@%(){}[\];* ]+))$/;
const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/; // EMAIL

const CITY_REGEX = /^(([ ]*)|([ A-Za-z0-9-_.:,'&/]|(&amp;))+)$/i;
const militaryCityRegex = /^[A-Z]{3}$/;
const stateRegex = /^(([ ]*)|(([ A-Za-z0-9\-_.,()&])|(&amp;))+)$/i;
const countryRegex = /^(([ ]*)|([ A-Za-z0-9\-_.,()'&]|(&amp;))+)$/i;
const zipRegex = /^(([ ]*)|([a-zA-Z0-9-]+))$/i;
const postalRegex = /^[A-Za-z0-9 -]*$/i;
const boxRegex =
  /^\s*((?:p(?:ost)?\.?\s?[o|0](?:\.|ffice)?)\b|(?:b(?:[o|0]x)|(?:in)))/i;
const poBoxRegex = /^\s*((P(ost)?[.]*\s*|o(ffice)?[.]*\s*|_*box_*){2,3})/i;
const birthdayRegex = /^(\d\d)(\d\d)(\d\d\d\d)$/;
const customAccountGroupNameRegex = /^(([ ]*)|([ A-Za-z0-9\-_;.()&/'$]+))$/;
export const SocialSecurityNumber = /^\d{4}$/;

// remove all non-digit characters from the input
export const handleInput = (e) => {
  const cleanedValue = e.target.value.replace(/\D/g, '');
  if (cleanedValue.length > 9) {
    e.target.value = cleanedValue.slice(0, 9); // Limit to 9 digits
  } else {
    e.target.value = cleanedValue;
  }
}

// first letter of each word is capitalized and the remaining letters are in lowercase
export const  capitalizeFirstLetter=(str)=> {
  return str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

const runValidation = (validation, errorMessage) => (data) => {
  const isValid = validation(data);
  return isValid ? true : errorMessage;
};

/* workaround for peculiar behaviour of runValidation():
   runValidation() never returns false (it returns an errorMsg if condition
   is not true) therefore it can't be easily used as a pure boolean check
   This method can take the output of runValidation() and returns false
   if condition is not met, true otherwise.
 */
export const convertValidationToBoolean = (validation) => {
  return validation === true;
};

export const handleMultipleValidations = (inputValue, validations) => {
  const validationErrors = validations.map((validation) =>
    validation(inputValue)
  );
  const errorIndex = validationErrors.findIndex((element) => element !== true);
  return errorIndex !== -1 ? validations[errorIndex](inputValue) : true;
};

/**
 * @param fns
 * This had to be pulled from innersource to be able to replicate the validation result
 * this should eventually be an enhancement to that repo
 */
export const runValidationFns = (fns, event) => {
  if (event) {
    const {
      target: { value }
    } = event;
    return fns.map((fn = noop) => fn(value));
  }
  return fns.map((fn = noop) => fn());
};

export const isFieldRequired = runValidation(
  (a) => a.length > 0,
  'Required Field'
);

export const isValidName = runValidation(
  (a) => a && a.trim().length >= 2 && NAME_REGEX.test(a.trim()),
  'Invalid Content'
);

export const isValidFirstLastName = runValidation(
  (a) => a && a.trim().length >= 2 && GVA_NAME_REGEX.test(a.trim()),
  'Invalid Content'
);

export const isValidNameLength = runValidation(
  (a) => a.length < 35,
  'Invalid Name'
);

export const isValidEntityNameLength = runValidation(
  (a) => a.length < 70,
  'Invalid Name'
);

const INVALID_PHONE_MSG = 'Invalid Phone Number';

export const isValidRequiredUsPhoneNumber = runValidation((a) => {
  return (
    a &&
    (a.length !== 0 || !isEmpty(a)) &&
    PHONE_REGEX.test(a) &&
    !REPETITION_REGEX.test(a) &&
    a.length === 10
  );
}, INVALID_PHONE_MSG);

export const isValidEmail = runValidation(
  (a) => a && !isEmpty(a) && EMAIL_REGEX.test(a) && a.length > 5,
  'Invalid Email Address'
);

export const isValidRelationShip = runValidation(
  (a) => a && a.length > 0 && RELATIONSHIP_REGEX.test(a),
  'Invalid Relationship'
);

export const isValidUsPhoneNumber = runValidation(
  (a) => !a || (a && a.length === 10),
  INVALID_PHONE_MSG
);

export const isValidNonUSPhoneNumber = runValidation(
  (a) => !a || (a && a.length <= 12),
  INVALID_PHONE_MSG
);

export const isPOBox = runValidation(
  (a) => !boxRegex.test(a) && !poBoxRegex.test(a),
  <div>
    P.O. Box addresses can only be updated by{' '}
    <a className="link" href={footerLinks.contactUsUrl}>
      calling
    </a>{' '}
    <TRP />
  </div>
);

export const isResidentialPOBox = runValidation(
  (a) => !boxRegex.test(a) && !poBoxRegex.test(a),
  <div>Residential/Legal address cannot be a PO Box</div>
);

export const isValidAddressOne = runValidation(
  (a) => a && a.length <= 35 && ADDRESS_LINE1_REGEX.test(a),
  'Invalid Address'
);

export const isValidAddressTwo = runValidation(
  (a) => !a || (a && a.length <= 35 && ADDRESS_LINE2_REGEX.test(a)),
  'Invalid Address'
);

export const isValidCity = runValidation(
  (a) => a && a.length <= 35 && CITY_REGEX.test(a),
  'Invalid City'
);

export const isValidMilitaryCity = runValidation(
  (a) => a && militaryCityRegex.test(a),
  'Invalid City'
);

export const isValidState = runValidation(
  (a) => a && stateRegex.test(a),
  'Invalid State'
);

export const isValidZip = runValidation(
  (a) => a && a.length === 5 && zipRegex.test(a),
  'Invalid Zip'
);

export const isValidZipExt = runValidation(
  (a) => !a || (a.length === 4 && zipRegex.test(a)),
  'Invalid Zip'
);

export const isValidPostalCode = runValidation(
  (a) => a && a.length <= 10 && postalRegex.test(a),
  'Invalid Postal Code'
);

export const isValidCountry = runValidation(
  (a) => a && countryRegex.test(a),
  'Invalid Country'
);

export const isValidTaxId = runValidation(
  (a) => !a || a.length === 9,
  'Invalid SSN/TIN'
);

export const isValidSSN = runValidation(
  (a) => {
    return !a || (a.length === 4 && SocialSecurityNumber.test(a))
  },
  'Invalid SSN/TIN'
);

export const isValidCustomAccountGroup = runValidation(
  (a) => {
    return a && a.length < 75 && customAccountGroupNameRegex.test(a)
  },
  'Invalid Group Name'
);

//If using formatted input component for SSN/TIN. Formatted component add the "-", so add 2 to SSN length
export const isValidTaxIdFormatted = runValidation(
  (a) => !a || a.length === 11,
  'Invalid SSN/TIN'
);

export const isValidBirthday = runValidation((a) => {
  if (!a) {
    return true;
  }
  const match = birthdayRegex.exec(a);
  if (match === null) {
    return false;
  }
  const month = Number.parseInt(match[1], 10) - 1;
  const day = Number.parseInt(match[2], 10);
  const year = Number.parseInt(match[3], 10);

  const parsed = new Date(year, month, day);

  return (
    !Number.isNaN(parsed.valueOf()) && year >= 1880 && parsed <= new Date()
  );
}, 'Invalid birthday');

const addressOneValidation = [isValidAddressOne, isPOBox];
const addressTwoValidation = [isValidAddressTwo, isPOBox];
const residentialAddressOneValidation = [isValidAddressOne, isResidentialPOBox];
const residentialAddressTwoValidation = [isValidAddressTwo, isResidentialPOBox];
const cityValidation = [isValidCity];
const stateValidation = [isValidState];
const zipCodeValidation = [isValidZip];
const zipCodeExtValidation = [isValidZipExt];
const validPhoneNumberValidation = [isValidUsPhoneNumber];

const secondaryAddressOneValidation = [isValidAddressOne];
const secondaryAddressTwoValidation = [isValidAddressTwo];

const isRequiredField = (arr) => [isFieldRequired].concat(arr);

export const contactInfoAddressValidation = {
  addressOne: isRequiredField(addressOneValidation),
  addressTwo: addressTwoValidation,
  city: isRequiredField(cityValidation),
  state: isRequiredField(stateValidation),
  zipCode: isRequiredField(zipCodeValidation),
  zipCodeExt: zipCodeExtValidation,
  daytimePhone: validPhoneNumberValidation,
  eveningPhone: validPhoneNumberValidation,
  mobilePhone: validPhoneNumberValidation,
  faxNumber: validPhoneNumberValidation
};

export const residentialAddressValidation = {
  ...contactInfoAddressValidation,
  addressOne: isRequiredField(residentialAddressOneValidation),
  addressTwo: residentialAddressTwoValidation,
}

export const contactInfoSecondaryAddressValidation = {
  addressOne: isRequiredField(secondaryAddressOneValidation),
  addressTwo: secondaryAddressTwoValidation,
  city: isRequiredField(cityValidation),
  state: isRequiredField(stateValidation),
  zipCode: isRequiredField(zipCodeValidation),
  zipCodeExt: zipCodeExtValidation,
  daytimePhone: validPhoneNumberValidation,
  eveningPhone: validPhoneNumberValidation,
  mobilePhone: validPhoneNumberValidation,
  faxNumber: validPhoneNumberValidation
};

export const trustedContactAddressValidation = {
  addressOne: addressOneValidation,
  addressTwo: addressTwoValidation,
  city: cityValidation,
  state: stateValidation,
  zipCode: zipCodeValidation,
  zipCodeExt: zipCodeExtValidation,
  daytimePhone: isRequiredField(validPhoneNumberValidation),
  eveningPhone: validPhoneNumberValidation,
  mobilePhone: validPhoneNumberValidation,
  faxNumber: validPhoneNumberValidation
};

export const checkAllFieldsValid = (validFields, invalidFields) =>
  !validFields.some(
    (field) =>
      invalidFields[field] &&
      (invalidFields[field].length || Object.keys(invalidFields[field]).length)
  );
